/**
 * 主站前台 - 新闻动态列表页
 * 路由: /news
 * 数据源: GET /api/cms/articles
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const CATEGORIES = [
  { key: '', label: '全部' },
  { key: 'news', label: '网站公告' },
  { key: 'industry', label: '行业动态' },
];

const CATEGORY_LABELS = { news: '网站公告', industry: '行业动态' };

export default function NewsPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');

  useEffect(() => {
    loadArticles();
  }, [activeCategory]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = activeCategory ? `?category=${activeCategory}` : '';
      const response = await api.get(`/cms/articles${params}`);
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('加载文章失败:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="pt-24 pb-20 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 页头 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">新闻动态</h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            了解 NCB 的最新进展、功能更新与行业洞察
          </p>
        </div>

        {/* 分类筛选 */}
        <div className="flex justify-center gap-3 mb-10">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat.key
                  ? 'gradient-bg text-white shadow-lg'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* 文章列表 */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">加载中...</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">暂无已发布的文章</p>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {articles.map(article => (
              <Link
                key={article.slug}
                to={`/news/${article.slug}`}
                className="group block bg-white/5 rounded-xl overflow-hidden border border-white/5 hover:border-white/20 hover:bg-white/10 transition-all duration-300"
              >
                {/* 封面图 */}
                {article.cover_image ? (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.cover_image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-blue-900/30 to-purple-900/30 flex items-center justify-center">
                    <span className="text-4xl opacity-30">📰</span>
                  </div>
                )}

                {/* 内容 */}
                <div className="p-5">
                  {/* 分类 + 日期 */}
                  <div className="flex items-center gap-3 mb-3">
                    {article.category && (
                      <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-500/20 text-blue-300">
                        {CATEGORY_LABELS[article.category] || article.category}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {formatDate(article.published_at)}
                    </span>
                  </div>

                  {/* 标题 */}
                  <h2 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors line-clamp-2">
                    {article.title}
                  </h2>

                  {/* 摘要 */}
                  {article.summary && (
                    <p className="text-sm text-gray-400 line-clamp-3">
                      {article.summary}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
